import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  useListOrganizationCategories,
  useListOrganizationCategoryFolders,
} from "~/api/organization/organization";
import { useOrganizationParams } from "~/helpers";

import { FolderPlus, Pencil, Plus } from "lucide-react";
import { UserRole } from "~/api/model";
import { Loader } from "~/components/Loader";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Button } from "~/components/ui/button";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import BulkUpdateModal from "./categories/BulkUpdate";
import CreateCategoryModal from "./categories/CreateCategoryModal";
import CreateFolderModal from "./categories/CreateFolderModal";
import { FolderList } from "./categories/FolderList";

export const SettingsCategories: FC = () => {
  useTrackPageVisit("app/settings_categories/visit");

  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] =
    useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);

  const { organizationId } = useOrganizationParams();
  const { data: categories, isLoading: isLoadingCategories } =
    useListOrganizationCategories(organizationId);
  const { data: folders, isLoading: isLoadingFolders } =
    useListOrganizationCategoryFolders(organizationId);

  if (isLoadingCategories || isLoadingFolders) {
    return (
      <div className="flex justify-center items-center w-[608px] h-[50vh]">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-6 mt-2 w-full pb-12">
        <div className="flex justify-between items-center gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-2xl font-semibold">
              <FormattedMessage id="settings.categoriesManagement" />
            </h3>
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.categoriesManagement.description"
                defaultMessage="Add the categories by which Sky AI will sort the documents. Detailed descriptions and instructions will help AI to work more efficiently."
              />
            </p>
          </div>
          <div className="flex items-center gap-2 self-start">
            <DisplayFor
              roles={[UserRole.SUPERADMIN, UserRole.OWNER, UserRole.MEMBER]}
            >
              <Button
                onClick={() => setIsBulkUpdateModalOpen(true)}
                variant="outline"
              >
                <Pencil className="w-4 h-4 mr-2" />
                <FormattedMessage id="settings.bulkUpdate.title" />
              </Button>
            </DisplayFor>
            <Button
              onClick={() => setIsCreateFolderModalOpen(true)}
              variant="outline"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              <FormattedMessage id="settings.createFolder" />
            </Button>
            <Button onClick={() => setIsCreateCategoryModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              <FormattedMessage id="settings.addCategory" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {categories && (
            <FolderList categories={categories} folders={folders || []} />
          )}
        </div>
      </div>
      {isCreateCategoryModalOpen && (
        <CreateCategoryModal
          open={isCreateCategoryModalOpen}
          onOpenChange={setIsCreateCategoryModalOpen}
        />
      )}
      {isBulkUpdateModalOpen && (
        <BulkUpdateModal
          open={isBulkUpdateModalOpen}
          onOpenChange={setIsBulkUpdateModalOpen}
        />
      )}
      {isCreateFolderModalOpen && (
        <CreateFolderModal
          open={isCreateFolderModalOpen}
          onOpenChange={setIsCreateFolderModalOpen}
        />
      )}
    </>
  );
};
